/**
 * Receipt printer commands (ESC/POS), made from a picture of the receipt.
 *
 * The receipt is drawn by the browser - Arabic shaped and joined properly, the
 * logo, the barcode - and sent to the printer as dots. Sending text instead is
 * what makes cheap thermal printers print Arabic as separated letters in the
 * wrong order; a picture cannot come out wrong. Every thermal receipt printer
 * worth the name understands the raster command used here (GS v 0).
 *
 * Pure functions, so the bytes can be checked in a test without a printer.
 */

/** Dots across the printable width: 72 mm on an 80 mm roll, 48 mm on 58 mm, at 8 dots per mm. */
export const PAPER_DOTS: Record<'80' | '58', number> = { '80': 576, '58': 384 };

/**
 * Colour pixels to black-or-white, 1 meaning black. Floyd-Steinberg dithering,
 * so a logo keeps its shading; text is pure black or white either way.
 */
export function toMono(rgba: Uint8ClampedArray | Uint8Array, width: number, height: number, threshold = 150): Uint8Array {
  const lum = new Float32Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const a = rgba[i * 4 + 3] / 255;
    // A see-through pixel is paper, not black.
    const l = 0.299 * rgba[i * 4] + 0.587 * rgba[i * 4 + 1] + 0.114 * rgba[i * 4 + 2];
    lum[i] = l * a + 255 * (1 - a);
  }
  const out = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      const old = lum[i];
      const black = old < threshold;
      out[i] = black ? 1 : 0;
      const err = old - (black ? 0 : 255);
      if (x + 1 < width) lum[i + 1] += (err * 7) / 16;
      if (y + 1 < height) {
        if (x > 0) lum[i + width - 1] += (err * 3) / 16;
        lum[i + width] += (err * 5) / 16;
        if (x + 1 < width) lum[i + width + 1] += err / 16;
      }
    }
  }
  return out;
}

/** The last row with anything black on it, so the paper stops where the receipt does. */
export function lastInkRow(mono: Uint8Array, width: number, height: number): number {
  for (let y = height - 1; y >= 0; y--) {
    for (let x = 0; x < width; x++) if (mono[y * width + x]) return y;
  }
  return -1;
}

export type DrawerPin = '2' | '5' | 'both';

/** ESC p m t1 t2 - the pulse that springs the drawer; m picks the pin it is wired to. */
export function drawerKick(pin: DrawerPin): number[] {
  const one = (m: number) => [0x1b, 0x70, m, 0x19, 0xfa];
  return pin === 'both' ? [...one(0), ...one(1)] : one(pin === '5' ? 1 : 0);
}

/** How many rows go in one raster command. Small enough for any printer's buffer. */
const BAND = 240;

/**
 * A whole print job: reset, the drawer pulse if asked for (first, so the drawer
 * opens while the receipt is still printing), the picture in bands, then feed
 * and cut.
 */
export function rasterJob(
  mono: Uint8Array,
  width: number,
  height: number,
  opts: { kick?: DrawerPin | null; cut?: boolean; feedLines?: number } = {},
): Uint8Array {
  const bytesPerRow = Math.ceil(width / 8);
  const out: number[] = [0x1b, 0x40]; // ESC @ - start clean
  if (opts.kick) out.push(...drawerKick(opts.kick));
  for (let top = 0; top < height; top += BAND) {
    const rows = Math.min(BAND, height - top);
    // GS v 0 m xL xH yL yH, then the dots, left to right, top to bottom, 8 to a byte.
    out.push(0x1d, 0x76, 0x30, 0x00, bytesPerRow & 0xff, bytesPerRow >> 8, rows & 0xff, rows >> 8);
    for (let y = top; y < top + rows; y++) {
      for (let bx = 0; bx < bytesPerRow; bx++) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = bx * 8 + bit;
          if (x < width && mono[y * width + x]) byte |= 0x80 >> bit;
        }
        out.push(byte);
      }
    }
  }
  out.push(0x1b, 0x64, opts.feedLines ?? 4); // ESC d n - feed past the cutter
  if (opts.cut !== false) out.push(0x1d, 0x56, 0x01); // GS V 1 - partial cut; ignored by printers with no cutter
  return Uint8Array.from(out);
}

/** A drawer pulse on its own, as a job. */
export function kickJob(pin: DrawerPin): Uint8Array {
  return Uint8Array.from([0x1b, 0x40, ...drawerKick(pin)]);
}

export function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}
